/* eslint-disable max-lines-per-function */
import { useCallback, useEffect, useState } from 'react';

import type { AbstractError } from '@/domain/errors/abstract-error.js';
import type { ReasonModel } from '@/domain/models/reason.js';
import type { ReasonController } from '@/presentation/controllers/reason-controller.js';
import type { ReasonCategory } from '@/shared/utils/format-status.js';

type StatusFilter = 'active' | 'inactive' | 'all';

type State = { reasons: ReasonModel[]; isLoading: boolean; error: string | null; toggleError: AbstractError | null };

export function useReasons(controller: ReasonController) {
    const [state, setState] = useState<State>({ reasons: [], isLoading: true, error: null, toggleError: null });
    const [activeCategory, setActiveCategory] = useState<ReasonCategory>('CANCELAMENTO');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');

    const load = useCallback(async () => {
        setState((s) => ({ ...s, isLoading: true, error: null, toggleError: null }));
        const isActive = statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;
        const result = await controller.loadReasons({ category: activeCategory, isActive });
        result.fold(
            (error) => setState({ reasons: [], isLoading: false, error: error.message, toggleError: null }),
            (reasons) => setState({ reasons, isLoading: false, error: null, toggleError: null })
        );
    }, [controller, activeCategory, statusFilter]);

    useEffect(() => {
        load();
    }, [load]);

    const createReason = useCallback(
        async (params: { label: string; category: ReasonCategory; sortOrder?: number }) => {
            const result = await controller.createReason(params);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const updateReason = useCallback(
        async (id: string, params: { label: string; category: ReasonCategory; sortOrder?: number }) => {
            const result = await controller.updateReason(id, params);
            if (result.isRight()) {
                await load();
            }
            return result.isRight();
        },
        [controller, load]
    );

    const toggleReason = useCallback(
        async (id: string, active: boolean) => {
            setState((s) => ({ ...s, toggleError: null }));
            const result = await controller.toggleReason(id, active);
            if (result.isRight()) {
                await load();
                return true;
            }
            setState((s) => ({ ...s, toggleError: result.value }));
            return false;
        },
        [controller, load]
    );

    const clearToggleError = useCallback(() => {
        setState((s) => ({ ...s, toggleError: null }));
    }, []);

    return { ...state, activeCategory, setActiveCategory, statusFilter, setStatusFilter, reload: load, createReason, updateReason, toggleReason, clearToggleError };
}
